/*
 * @providesModule SmsMarketingCheckbox
 */

import {Linking, Text, TouchableOpacity, View, ViewStyle} from 'react-native';
import SkillzImage from 'SkillzImage';
import {_m} from 'INTL';
import URLConstants from '../../Utilities/Constants/URLConstants';

import styles from './SmsMarketingCheckboxStyle';
import {useState} from 'react';
import {StyleProp} from 'react-native/Libraries/StyleSheet/StyleSheet';
import {TextStyle} from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import CurrentUserStore from '../../Stores/CurrentUser/CurrentUserStore';
import SkillzStyle from 'SkillzStyle';
import CommonStyles from 'CommonStyles';

type SmsMarketingCheckboxProps = {
  textStyle?: StyleProp<TextStyle>;
  wrapperStyle?: StyleProp<ViewStyle>;
  onPress?: ((isChecked: boolean) => void) | undefined;
  onToggle?: ((isChecked: boolean) => void) | undefined;
  showCheckbox?: boolean;
  isNewUI?: boolean;
};

const SmsMarketingCheckbox = (props: SmsMarketingCheckboxProps) => {
  const {textStyle, wrapperStyle, onPress, onToggle, showCheckbox = true} = props;
  const [isChecked, setIsChecked] = useState(false);

  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;
  const theme = SkillzStyle.getOverriddenFallbackTheme();

  const {textColorPrimary, textColorSecondary, cashIconGradient} =
    SkillzStyle.getOverriddenFallbackTheme();

  const toggleChecked = () => {
    const newState = !isChecked;
    if (onPress) {
      onPress(newState);
    }
    if (onToggle) {
      onToggle(newState);
    }
    setIsChecked(newState);
  };

  if (!showCheckbox) {
    return null;
  }

  return (
    <View style={wrapperStyle}>
      <TouchableOpacity
        style={[styles.checkboxContainer, props.isNewUI && {left: 0, top: -16}]}
        onPress={toggleChecked}
        accessibilityRole={'button'}
        accessible={true}
        accessibilityLabel={'opt_in to sms marketing'}
      >
        <View
          style={[
            styles.checkboxWrapper,
            props.isNewUI && {
              width: 22,
              height: 22,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: cashIconGradient?.[0],
              backgroundColor: 'transparent',
              ...CommonStyles.justifyCenter,
              ...CommonStyles.itemsCenter,
              marginRight: 10,
            },
          ]}
        >
          {isChecked && (
            <SkillzImage
              source={'ic_checkmark_green_skz.png'}
              color={
                props.isNewUI
                  ? theme?.cashIconGradient?.[0] ?? 'rgba(212, 175, 55, 1)'
                  : 'rgb(74, 144, 226)'
              }
              resizeMode={'contain'}
              style={[styles.checkboxIcon]}
            />
          )}
        </View>
      </TouchableOpacity>
      <TouchableOpacity onPress={toggleChecked} activeOpacity={1}>
        {props.isNewUI ? (
          <Text
            style={[
              styles.message,
              props.isNewUI && {
                color: isDarkTheme ? textColorPrimary?.color : textColorSecondary?.color,
                marginLeft: 30,
              },
            ]}
            selectable={false}
          >
            {_m(
              'I agree to receive special offers and marketing text messages from Skillz at the phone number provided.*'
            )}
          </Text>
        ) : (
          <Text
            style={[
              styles.text,
              textStyle,
              props.isNewUI && {color: SkillzStyle.fullStyles.textColorPrimary.color},
            ]}
          >
            {_m(
              'By entering my contact information and clicking the "Next" button, I (1) agree to the Skillz '
            )}
            <Text
              style={styles.link}
              onPress={() => Linking.openURL(URLConstants.USER_TERMS_AND_CONDITIONS)}
            >
              {_m('User Terms and Conditions of Service')}
            </Text>
            {_m(', including Section 1.7 (Texting Terms and Conditions), and the Skillz ')}
            <Text style={styles.link} onPress={() => Linking.openURL(URLConstants.PRIVACY_POLICY)}>
              {_m('Privacy Policy')}
            </Text>
            {_m(
              ' and (2) consent to receiving text messages and automated phone calls, including account notifications and alerts, from or on behalf of Skillz. I understand this consent is not required to purchase goods or services and I may withdraw consent any time by texting STOP or contacting at '
            )}
            <Text style={styles.link} onPress={() => Linking.openURL(URLConstants.SUPPORT_EMAIL)}>
              support@skillz.com
            </Text>
            {_m(
              ". I may also text HELP to receive help. I understand that my carrier's message and data rates apply to my receipt of texts/calls and message frequency may vary."
            )}
          </Text>
        )}
      </TouchableOpacity>
    </View>
  );
};

export default SmsMarketingCheckbox;
